import {
	type EntryPointRef,
	type FlightLookupResult,
	MIN_CONNECTION_MINUTES,
	TRAVEL_CONNECTIONS_MAX,
	type FlightLookupRoute,
	type FlightSummary,
	TRAVEL_ORIGIN_MAX,
	TRAVEL_REMARKS_MAX,
	TRAVEL_SERVICE_NO_MAX,
	type TravelDirection,
	type TravelFormMessages,
	type TravelLeg,
	type TravelLegBody,
	type TravelMode,
	type TravelTransportType,
	TRAVEL_TRANSPORT_TYPE_OTHER_MAX,
	defaultTransportType,
	formatFlightDesignator,
	legNeedsFlightNumber,
	transportTypeFits,
	isConnectionAtRisk,
	legFlightChain,
	parseFlightDesignator,
} from "@seagames/contracts";

/**
 * One travel leg (an arrival or a departure of ONE card) as the leg form holds
 * it, and its translation to the wire (`TravelLegBody`, contracts `travel.ts`).
 *
 * Times are kept as instants (`scheduled_at` ISO), never as a wall clock, so
 * nothing outside the form needs the games' time zone: the form reads and
 * writes the wall clock in `games.timezone` itself (PLAN M9 T6). The date is
 * `travel_date`, already a date in that zone.
 *
 * Display-only copies ride along (`entry_point`, `flight`) so an inactive
 * entry point or a looked-up flight can be shown without another request.
 */
export interface TravelLegDraft {
	mode: TravelMode | "";
	/** How they travel within the mode (Commercial flight, Car, Cruise…); null = not said (an older leg). */
	transport_type: TravelTransportType | null;
	/** "Others (please specify)": what it is. Sent only with OTHER. */
	transport_type_other: string;
	/** The picked entry point (AIR → an airport, LAND → a crossing, SEA → a terminal). */
	entry_point: EntryPointRef | null;
	/** `YYYY-MM-DD` in the games' zone; "" until picked. */
	travel_date: string;
	/** The time, when known; null = "time not known yet". */
	scheduled_at: string | null;
	/**
	 * A host desk's manual delay on a leg with no flight row. The form never
	 * shows it; it is carried so an edit elsewhere in the leg does not wipe it
	 * (a leg is always sent whole), and dropped when the schedule itself
	 * changes (date, time, mode, service, flight) — a new schedule has no delay.
	 */
	delayed_to: string | null;
	/** AIR, from `GET /flights/lookup`: the shared `flights` row. Cleared when the flight number is edited. */
	flight_id: string | null;
	flight: FlightSummary | null;
	/** AIR: the flight as typed ("MH 803"). LAND / SEA: the coach, train or ferry. */
	service_no: string;
	/** Where they come from (ARRIVAL) / go to (DEPARTURE). */
	origin: string;
	needs_transport: boolean;
	remarks: string;
	/**
	 * Transit (AIR on a flight only): the connecting flights, in TRAVEL ORDER
	 * (ARRIVAL: flown before `flight`; DEPARTURE: after it). `[]` = direct —
	 * "+ Add a connecting flight" starts it; transit exactly when this is not empty.
	 */
	connections: ConnectionDraft[];
}

/** One connecting flight of a transit leg, as the form holds it. Only a looked-up `flight` is ever sent. */
export interface ConnectionDraft {
	/** React's list key (never sent). */
	key: string;
	/** The flight as typed ("Z2 501"). */
	service_no: string;
	/** From `GET /flights/lookup?role=CONNECTION`. Cleared when the number is edited. */
	flight: FlightSummary | null;
}

let connectionSeq = 0;
/** A new connection row (the first one comes from "+ Add a connecting flight"). */
export function emptyConnection(service_no = ""): ConnectionDraft {
	connectionSeq += 1;
	return { key: `cx${connectionSeq}`, service_no, flight: null };
}

export const TRAVEL_LEG_FIELDS = ["mode", "transport_type", "transport_type_other", "entry_point_id", "travel_date", "scheduled_at", "flight_id", "service_no", "origin", "needs_transport", "remarks", "connections"] as const;
export type TravelLegField = (typeof TRAVEL_LEG_FIELDS)[number];
/**
 * A flight of a transit leg, by its 0-based index in TRAVEL ORDER (the
 * main flight included) — what CONNECTION_* errors' `details.position` say.
 * Its DOM id is `${idPrefix}-chain.N` (the message under that flight).
 */
export type ChainErrorKey = `chain.${number}`;
export type TravelLegErrorKey = TravelLegField | ChainErrorKey;
export type TravelLegErrors = Partial<Record<TravelLegErrorKey, string>>;

export const isTravelLegField = (v: unknown): v is TravelLegField => typeof v === "string" && (TRAVEL_LEG_FIELDS as readonly string[]).includes(v);
export const isTravelLegErrorKey = (v: unknown): v is TravelLegErrorKey => isTravelLegField(v) || (typeof v === "string" && /^chain\.\d+$/.test(v));
export const chainErrorKey = (position: number): ChainErrorKey => `chain.${position}`;

/** A new leg: her default is air with a Games transfer. */
export function emptyLegDraft(): TravelLegDraft {
	return {
		mode: "AIR",
		transport_type: defaultTransportType("AIR"),
		transport_type_other: "",
		entry_point: null,
		travel_date: "",
		scheduled_at: null,
		delayed_to: null,
		flight_id: null,
		flight: null,
		service_no: "",
		origin: "",
		needs_transport: true,
		remarks: "",
		connections: [],
	};
}

/** A stored leg (`GET /accreditations/:id` → `travel.arrival`) → the form. */
export function legDraftFromLeg(leg: TravelLeg): TravelLegDraft {
	return {
		mode: leg.mode,
		transport_type: leg.transport_type ?? null,
		transport_type_other: leg.transport_type_other ?? "",
		entry_point: leg.entry_point,
		travel_date: leg.travel_date.slice(0, 10),
		scheduled_at: leg.scheduled_at,
		delayed_to: leg.flight ? null : leg.delayed_to,
		flight_id: leg.flight?.flight_id ?? null,
		flight: leg.flight,
		service_no: leg.service_no ?? (leg.flight ? formatFlightDesignator(leg.flight.carrier_code, leg.flight.flight_number) : ""),
		origin: leg.origin ?? "",
		needs_transport: leg.needs_transport,
		remarks: leg.remarks ?? "",
		connections: (leg.connections ?? []).map((f) => ({ ...emptyConnection(formatFlightDesignator(f.carrier_code, f.flight_number)), flight: f })),
	};
}

const blank = (v: string): string | null => (v.trim() === "" ? null : v.trim());

/**
 * The form → the wire leg (always WHOLE: an omitted field is stored as NULL).
 * On a flight row the flight is the identity (`service_no` null, as the
 * contract says); with no row the typed flight is kept in `service_no`,
 * normalised ("mh803" → "MH 803") when it reads as one.
 */
export function legDraftToBody(d: TravelLegDraft): TravelLegBody {
	const air = d.mode === "AIR";
	const typed = blank(d.service_no);
	const designator = air && typed ? parseFlightDesignator(typed) : null;
	const onFlight = air && d.flight_id !== null;
	return {
		mode: (d.mode || "AIR") as TravelMode,
		transport_type: d.transport_type,
		transport_type_other: d.transport_type === "OTHER" ? blank(d.transport_type_other) : null,
		entry_point_id: d.entry_point?.entry_point_id ?? "",
		travel_date: d.travel_date,
		scheduled_at: d.scheduled_at,
		delayed_to: onFlight ? null : d.delayed_to,
		flight_id: onFlight ? d.flight_id : null,
		service_no: onFlight ? null : designator ? formatFlightDesignator(designator.carrier_code, designator.flight_number) : typed,
		origin: blank(d.origin),
		needs_transport: d.needs_transport,
		remarks: blank(d.remarks),
		// ALWAYS sent: the leg is written whole, so an omitted list would wipe the stored connections.
		connections: onFlight ? d.connections.flatMap((c) => (c.flight ? [{ flight_id: c.flight.flight_id }] : [])) : [],
	};
}

/**
 * What the form checks before a save, in the viewer's language. The server
 * checks the same and more (the entry point's type against the mode) and has
 * the last word; this only saves a round trip on the common misses.
 */
export function validateLegDraft(
	d: TravelLegDraft,
	m: Pick<TravelFormMessages, "required" | "textTooLong" | "dateInvalid" | "flightNumberRequired" | "connectionsNeedFlight" | "transportTypeOtherRequired" | "transportTypeMismatch"> &
		Partial<ConnectionMessages>,
	/** The leg's direction: with it, the transit chain is checked too (`chainErrors`). */
	direction?: TravelDirection,
): TravelLegErrors {
	const e: TravelLegErrors = {};
	if (!d.mode) e.mode = m.required;
	// A ticket is a must for air (user, 2026-10-15): the flight number is what the schedules, the
	// welcome desk and transport key on. The server refuses it too.
	// A private / cargo flight may have none (user, 2026-10-31).
	if (d.mode && legNeedsFlightNumber(d.mode, d.transport_type) && !d.service_no.trim()) e.service_no = m.flightNumberRequired ?? m.required;
	if (d.mode && !transportTypeFits(d.mode, d.transport_type)) e.transport_type = m.transportTypeMismatch ?? m.required;
	else if (d.transport_type === "OTHER" && !d.transport_type_other.trim()) e.transport_type_other = m.transportTypeOtherRequired ?? m.required;
	else if (d.transport_type_other.trim().length > TRAVEL_TRANSPORT_TYPE_OTHER_MAX) e.transport_type_other = m.textTooLong;
	if (!d.entry_point) e.entry_point_id = m.required;
	if (!d.travel_date) e.travel_date = m.required;
	else if (!/^\d{4}-\d{2}-\d{2}$/.test(d.travel_date)) e.travel_date = m.dateInvalid;
	if (d.service_no.trim().length > TRAVEL_SERVICE_NO_MAX) e.service_no = m.textTooLong;
	if (d.origin.trim().length > TRAVEL_ORIGIN_MAX) e.origin = m.textTooLong;
	if (d.remarks.trim().length > TRAVEL_REMARKS_MAX) e.remarks = m.textTooLong;
	// Connections ride on the main flight (the server refuses them without it); `legDraftToBody` would drop them silently.
	if (d.mode === "AIR" && d.connections.length > 0 && !d.flight_id) e.connections = m.connectionsNeedFlight ?? "Find the main flight before adding connecting flights";
	if (direction && d.mode === "AIR") Object.assign(e, chainErrors(direction, d, m));
	return e;
}

// ── Transit ─────────────────────────────────────────────────────────────────

/** What the chain checks say, in the viewer's language (English defaults). */
export interface ConnectionMessages {
	/** A connection row with no looked-up flight (only a looked-up flight can be stored). */
	connectionNeedsLookup: string;
	/** = errors.CONNECTION_ROUTE_MISMATCH */
	connectionRoute: string;
	/** = errors.CONNECTION_TIME_ORDER */
	connectionTime: string;
}
export const enConnectionMessages: ConnectionMessages = {
	connectionNeedsLookup: "Find this flight and pick the day it flies",
	connectionRoute: "This flight doesn't leave from the airport the previous flight lands at.",
	connectionTime: "This flight leaves before the previous flight lands.",
};

/** One flight of the leg's chain, in travel order: the main flight or connection `index`. */
export type ChainRow = { kind: "main"; flight: FlightSummary | null } | { kind: "connection"; index: number; flight: FlightSummary | null };

/** The leg's flights in travel order (ARRIVAL: connections, then the main flight; DEPARTURE: the main flight, then connections). */
export function draftChain(direction: TravelDirection, d: Pick<TravelLegDraft, "flight" | "connections">): ChainRow[] {
	const main: ChainRow = { kind: "main", flight: d.flight };
	return legFlightChain<ChainRow>(
		direction,
		main,
		d.connections.map((c, index) => ({ kind: "connection", index, flight: c.flight })),
	);
}

/** Where `chain[i]` sits in travel order, for connection `index`. */
export const connectionPosition = (direction: TravelDirection, index: number): number => (direction === "ARRIVAL" ? index : index + 1);
/** The main flight's position in travel order. */
export const mainPosition = (direction: TravelDirection, connections: number): number => (direction === "ARRIVAL" ? connections : 0);

const at = (v: string | null | undefined): number | null => {
	if (!v) return null;
	const n = Date.parse(v);
	return Number.isFinite(n) ? n : null;
};

/**
 * The change between two consecutive flights of a chain:
 *   iata / name    where it happens (the earlier flight's destination; the later one's origin name)
 *   minutes        expected landing → expected take-off (scheduled when not expected); null when a time is missing
 *   routeMismatch  the later flight does not leave from where the earlier lands (server: CONNECTION_ROUTE_MISMATCH)
 *   timeOrder      it is SCHEDULED to leave before the earlier one is scheduled to land (server: CONNECTION_TIME_ORDER)
 *   tight          less than MIN_CONNECTION_MINUTES on expected times (the `connection_at_risk` rule)
 */
export interface Junction {
	iata: string | null;
	name: string | null;
	nextIata: string | null;
	minutes: number | null;
	routeMismatch: boolean;
	timeOrder: boolean;
	tight: boolean;
}
export function junctionOf(prev: FlightSummary, next: FlightSummary): Junction {
	const landed = at(prev.expected_arrival_at ?? prev.scheduled_arrival_at);
	const leaves = at(next.expected_departure_at ?? next.scheduled_departure_at);
	const minutes = landed !== null && leaves !== null ? Math.round((leaves - landed) / 60_000) : null;
	const sLanded = at(prev.scheduled_arrival_at);
	const sLeaves = at(next.scheduled_departure_at);
	const eLanded = at(prev.expected_arrival_at);
	const eLeaves = at(next.expected_departure_at);
	const iata = prev.destination_iata;
	return {
		iata,
		name: next.origin_iata && next.origin_iata === iata ? next.origin_name : null,
		nextIata: next.origin_iata,
		minutes,
		routeMismatch: !!iata && !!next.origin_iata && iata.toUpperCase() !== next.origin_iata.toUpperCase(),
		timeOrder: sLanded !== null && sLeaves !== null && sLeaves < sLanded,
		tight: eLanded !== null && eLeaves !== null && eLanded + MIN_CONNECTION_MINUTES * 60_000 > eLeaves,
	};
}

/** "2 h 05 min" as numbers (the caller words it). */
export const splitMinutes = (minutes: number): { hours: number; minutes: string } => {
	const m = Math.max(0, minutes);
	return { hours: Math.floor(m / 60), minutes: String(m % 60).padStart(2, "0") };
};

/**
 * The chain checks a save makes before it goes (the server makes the same
 * and has the last word), keyed by travel-order position: a connection row
 * not looked up, then — between looked-up neighbours — a route that does not
 * join, then a take-off before the previous landing. A tight change is NOT an
 * error (the server never refuses it): it is only flagged.
 */
export function chainErrors(direction: TravelDirection, d: Pick<TravelLegDraft, "flight" | "connections">, m: Partial<ConnectionMessages> = {}): TravelLegErrors {
	const msg = { ...enConnectionMessages, ...m };
	const e: TravelLegErrors = {};
	if (d.connections.length === 0) return e;
	const chain = draftChain(direction, d);
	chain.forEach((row, i) => {
		if (row.kind === "connection" && !row.flight) e[chainErrorKey(i)] = msg.connectionNeedsLookup;
	});
	for (let i = 1; i < chain.length; i++) {
		const prev = chain[i - 1]!.flight;
		const next = chain[i]!.flight;
		if (!prev || !next || e[chainErrorKey(i)]) continue;
		const j = junctionOf(prev, next);
		if (j.routeMismatch) e[chainErrorKey(i)] = msg.connectionRoute;
		else if (j.timeOrder) e[chainErrorKey(i)] = msg.connectionTime;
	}
	return e;
}

/** `connection_at_risk` on the draft (the contract's rule), for the preview before a save. */
export function draftConnectionAtRisk(direction: TravelDirection, d: Pick<TravelLegDraft, "flight" | "connections">): boolean {
	const chain = draftChain(direction, d).flatMap((r) => (r.flight ? [{ ...r.flight, connection: r.kind === "connection" }] : []));
	return isConnectionAtRisk(chain);
}

/** The airports a leg changes planes at, in travel order ("BKI", "SIN") — for "via BKI, SIN". */
export function viaAirports(direction: TravelDirection, main: FlightSummary | null, connections: readonly FlightSummary[]): string[] {
	if (connections.length === 0) return [];
	const chain = legFlightChain(direction, main, connections);
	const out: string[] = [];
	for (let i = 0; i < chain.length - 1; i++) {
		const code = chain[i]!.destination_iata ?? chain[i + 1]!.origin_iata;
		if (code && !out.includes(code)) out.push(code);
	}
	return out;
}

/**
 * Where the leg comes from (ARRIVAL) / goes to (DEPARTURE) once its whole
 * chain is known: the first flight's origin / the last flight's destination.
 * Null when the end of the chain is not looked up yet.
 */
export function chainOrigin(direction: TravelDirection, d: Pick<TravelLegDraft, "flight" | "connections">): string | null {
	const chain = draftChain(direction, d);
	const end = direction === "ARRIVAL" ? chain[0] : chain[chain.length - 1];
	const f = end?.flight;
	if (!f) return null;
	return direction === "ARRIVAL" ? (f.origin_name ?? f.origin_iata) : f.destination_iata;
}

/**
 * The day a flight's calendar opens on (`YYYY-MM-DD`): the day the previous
 * flight of the chain lands (`toDay` reads the instant in the games' zone),
 * else the day the next one departs. Null = the calendar's own default.
 */
export function calendarStartFor(chain: readonly ChainRow[], position: number, toDay: (iso: string) => string | null): string | null {
	const prev = chain[position - 1]?.flight;
	const prevAt = prev ? (prev.expected_arrival_at ?? prev.scheduled_arrival_at) : null;
	if (prevAt) return toDay(prevAt);
	const next = chain[position + 1]?.flight;
	if (next) return next.service_date;
	return null;
}

/** Room for another connection. */
export const canAddConnection = (d: Pick<TravelLegDraft, "connections">): boolean => d.connections.length < TRAVEL_CONNECTIONS_MAX;

/**
 * A CONNECTION_ROUTE_MISMATCH / CONNECTION_TIME_ORDER refusal → the leg and
 * the flight it is about (`details.direction`, `details.position`). Null for
 * any other error, or details the build cannot read.
 */
export function connectionErrorTarget(code: string, details: Record<string, unknown> | null | undefined): { direction: TravelDirection; key: ChainErrorKey } | null {
	if (code !== "CONNECTION_ROUTE_MISMATCH" && code !== "CONNECTION_TIME_ORDER") return null;
	const direction = details?.["direction"];
	const position = Number(details?.["position"]);
	if ((direction !== "ARRIVAL" && direction !== "DEPARTURE") || !Number.isInteger(position) || position < 0) return null;
	return { direction, key: chainErrorKey(position) };
}

/** Both legs of one card: a departure dated before the arrival (the contract's own rule, read on drafts). */
export const departureBeforeArrival = (arrival: TravelLegDraft | null, departure: TravelLegDraft | null): boolean =>
	!!arrival?.travel_date && !!departure?.travel_date && departure.travel_date < arrival.travel_date;

/**
 * The fields of a leg that ARE its schedule: changing any of them makes a
 * host's manual delay (`delayed_to`) meaningless, so it is dropped.
 */
export const SCHEDULE_FIELDS = ["mode", "travel_date", "scheduled_at", "service_no", "flight_id"] as const satisfies readonly (keyof TravelLegDraft)[];

/** The time a leg is expected (arrives / leaves), the way the server's trigger computes `expected_at`. */
export function expectedOf(direction: TravelDirection, d: Pick<TravelLegDraft, "flight" | "scheduled_at" | "delayed_to">): { scheduled: string | null; expected: string | null } {
	if (d.flight) {
		const scheduled = direction === "ARRIVAL" ? d.flight.scheduled_arrival_at : d.flight.scheduled_departure_at;
		const expected = direction === "ARRIVAL" ? d.flight.expected_arrival_at : d.flight.expected_departure_at;
		return { scheduled: scheduled ?? d.scheduled_at, expected: expected ?? scheduled ?? d.scheduled_at };
	}
	return { scheduled: d.scheduled_at, expected: d.delayed_to ?? d.scheduled_at };
}

/** Two instants that differ by a minute or more (a re-time worth striking through). */
export const changedTime = (a: string | null, b: string | null): boolean => !!a && !!b && Math.abs(Date.parse(a) - Date.parse(b)) >= 60_000;

/** A terminal as printed after the airport code: the provider says "2" or "T2"; both read "T2". */
export const terminalLabel = (terminal: string): string => (/^\d+[A-Z]?$/i.test(terminal.trim()) ? `T${terminal.trim()}` : terminal.trim());

/**
 * AIR legs keep the entry point, time and origin hidden until needed: the
 * flight lookup fills them. What a lookup answered, as far as this decision
 * needs it (a subset of `FlightLookupState`, kept here so this stays pure).
 */
export type LookupOutcome = { kind: "idle" | "loading" | "found" } | { kind: "miss"; reason: string };

/** The AIR fields that are hidden until needed. */
export const AIR_DETAIL_FIELDS = ["entry_point_id", "scheduled_at", "origin"] as const satisfies readonly TravelLegField[];

/**
 * Which of them the details offer: with a flight attached the time and
 * origin ARE the flight's (shown on the flight card, never typed), so only
 * the entry point — in case the terminal guess is wrong.
 */
export const airDetailFields = (d: Pick<TravelLegDraft, "flight">): readonly TravelLegField[] => (d.flight ? ["entry_point_id"] : AIR_DETAIL_FIELDS);

// ── The AIR date field ──────────────────────────────────────────────────────

/**
 * What `GET /flights/dates` said about the flight number in the field (the
 * hook maps the wire and the errors onto this):
 *   idle     not asked (nothing committed yet, or another number is in the field)
 *   loading  asking
 *   found    the departure dates it flies (may be empty)
 *   miss     TRACKING_OFF / NOT_FOUND (the answer), UNAVAILABLE (503 or any
 *            other failure), RATE_LIMITED (429)
 */
export type FlightDatesMissReason = "TRACKING_OFF" | "NOT_FOUND" | "UNAVAILABLE" | "RATE_LIMITED";
/** The travel window the dates were asked for (`YYYY-MM-DD`, both included) — known whenever the server answered. */
export interface FlightDatesWindow {
	from: string;
	to: string;
}
export type FlightDatesOutcome =
	| { kind: "idle" }
	| { kind: "loading" }
	| { kind: "found"; dates: readonly string[]; window?: FlightDatesWindow }
	| { kind: "miss"; reason: FlightDatesMissReason; window?: FlightDatesWindow };

/**
 * A day picked in the calendar that the lookup could not turn into a flight.
 * The schedules said it flies that day, so this is NOT a reason to drop to
 * typed dates: the calendar stays, with the reason under it.
 *   NOT_OUR_AIRPORT  it flies that day, but not to (ARRIVAL) / from (DEPARTURE) a Games
 *                    airport — `route` is where it goes, `airports` are ours
 *   NOT_FOUND        the provider has no such flight that day after all
 *   UNAVAILABLE / RATE_LIMITED  503 / 429: try again, or type it
 */
export type PickedDayMissReason = "NOT_OUR_AIRPORT" | "NOT_FOUND" | "UNAVAILABLE" | "RATE_LIMITED";
export interface PickedDayMiss {
	/** The flight number it was for (`designatorKey`): another number forgets it. */
	key: string;
	day: string;
	reason: PickedDayMissReason;
	route?: FlightLookupRoute;
	airports?: readonly string[];
}

/**
 * What a picked day's lookup miss leads to: stay on the calendar with the
 * reason (`stay`), or — tracking switched off since the dates were fetched —
 * the typed fallback (`typed`). `result` is null on 503 / 429; `failure` is
 * then what the hook made of the error.
 */
export function pickedDayMiss(
	key: string,
	day: string,
	result: Pick<FlightLookupResult, "reason" | "route" | "airports"> | null,
	failure: "UNAVAILABLE" | "RATE_LIMITED" = "UNAVAILABLE",
): { kind: "stay"; miss: PickedDayMiss } | { kind: "typed"; reason: TypedDateReason } {
	if (!result) return { kind: "stay", miss: { key, day, reason: failure } };
	if (result.reason === "TRACKING_OFF") return { kind: "typed", reason: "TRACKING_OFF" };
	if (result.reason === "NOT_OUR_AIRPORT")
		return { kind: "stay", miss: { key, day, reason: "NOT_OUR_AIRPORT", ...(result.route ? { route: result.route } : {}), ...(result.airports ? { airports: result.airports } : {}) } };
	return { kind: "stay", miss: { key, day, reason: "NOT_FOUND" } };
}

/**
 * Why the date (and the time, origin, entry point) are typed by hand:
 * a dates miss; NO_DATES (it does not fly in the travel window); UNREADABLE
 * (not a flight number the schedules can know, like "TBA 1"); MANUAL (an
 * existing leg typed by hand — nothing to explain).
 */
export type TypedDateReason = FlightDatesMissReason | "NO_DATES" | "UNREADABLE" | "MANUAL";

/**
 * How the AIR date field reads:
 *   await     no flight number committed yet (typed, not blurred): the picker waits
 *   loading   fetching the days it flies
 *   picker    pick one of `dates` (the flight's DEPARTURE date); a picked day is looked up.
 *             `miss`: the last picked day could not be had (`PickedDayMiss`) — still the
 *             picker, with the reason under it and a way into the typed fallback
 *   attached  a flight is on the leg, its days not fetched yet: its date, opening the picker asks
 *   fixed     a flight is on the leg, its days could not be fetched: its date read-only + "Change"
 *   typed     the plain typed date + time + entry point + origin (the fallback): the lookup is
 *             an accelerator, never a gate
 */
export type AirDateMode =
	| { kind: "await" }
	| { kind: "loading" }
	| { kind: "picker"; dates: readonly string[]; miss?: PickedDayMiss }
	| { kind: "attached" }
	| { kind: "fixed"; reason: FlightDatesMissReason }
	| { kind: "typed"; reason: TypedDateReason; window?: FlightDatesWindow };

/**
 * The AIR date field's mode.
 *   `committed`  the number in the field is the one last blurred / entered (not mid-typing)
 *   `dates`      the dates outcome FOR THAT number
 *   `manual`     a fallback chosen for that number ("Change", "Enter it yourself instead",
 *                tracking switched off under a pick, an existing hand-typed leg); wins over everything
 *   `pickMiss`   the last picked day's miss, kept only for the number it was for; shown on
 *                the picker (a picked-day miss never drops to typed dates by itself)
 * Only a dates-level miss (tracking off, not in the schedules, down, rate-limited, no dates in
 * the window, an unreadable number) goes straight to typed.
 */
export function airDateMode(
	d: Pick<TravelLegDraft, "flight" | "service_no">,
	input: { committed: boolean; dates: FlightDatesOutcome; manual: TypedDateReason | null; pickMiss?: PickedDayMiss | null },
): AirDateMode {
	const { dates, manual } = input;
	const pickMiss = input.pickMiss && input.pickMiss.key === designatorKey(d.service_no) ? input.pickMiss : null;
	const window = dates.kind === "found" || dates.kind === "miss" ? dates.window : undefined;
	const typedIn = (reason: TypedDateReason): AirDateMode => (window ? { kind: "typed", reason, window } : { kind: "typed", reason });
	if (manual) return { kind: "typed", reason: manual };
	if (d.flight) {
		if (dates.kind === "found") return { kind: "picker", dates: dates.dates };
		if (dates.kind === "loading") return { kind: "loading" };
		if (dates.kind === "miss") return { kind: "fixed", reason: dates.reason };
		return { kind: "attached" };
	}
	const typed = d.service_no.trim();
	if (!typed || !input.committed) return { kind: "await" };
	if (!parseFlightDesignator(typed)) return { kind: "typed", reason: "UNREADABLE" };
	switch (dates.kind) {
		case "idle":
			return { kind: "await" };
		case "loading":
			return { kind: "loading" };
		case "found":
			if (!dates.dates.length) return typedIn("NO_DATES");
			return pickMiss ? { kind: "picker", dates: dates.dates, miss: pickMiss } : { kind: "picker", dates: dates.dates };
		case "miss":
			return typedIn(dates.reason);
	}
}

/**
 * The fallback a form starts in, for the number it starts with: an existing
 * AIR leg with no flight but a date or details typed was entered by hand —
 * show it the way it was entered, not an empty picker. Null otherwise.
 */
export function initialManual(d: TravelLegDraft): TypedDateReason | null {
	if (d.mode !== "AIR" || d.flight_id) return null;
	return d.travel_date || d.scheduled_at || d.origin.trim() ? "MANUAL" : null;
}

/** The key a flight number is remembered by ("mh 0602" → "MH602"); the trimmed upper text when it is not one. */
export function designatorKey(text: string): string {
	const p = parseFlightDesignator(text.trim());
	return p ? `${p.carrier_code}${p.flight_number}` : text.trim().toUpperCase();
}

/**
 * Whether the details must open now, while the person works: they have to
 * see or fill something there. Never true just because a flight is attached
 * — then the flight card shows what was filled. Land and sea always show
 * their fields.
 *   - the date is typed (the fallback): time, entry point and origin are typed too
 *   - the lookup missed (tracking off, not found, down, rate-limited, unreadable)
 *     — "pick the date" is not about the details, the date is in view; nor is a
 *     picked day's miss, which keeps the calendar
 *   - a field behind it has an error (a failed save must show it)
 *   - a flight is attached but left the entry point empty (no terminal match)
 */
export function detailsMustOpen(d: TravelLegDraft, lookup: LookupOutcome, errors: TravelLegErrors, dateMode: AirDateMode = { kind: "await" }): boolean {
	if (d.mode !== "AIR") return true;
	if (dateMode.kind === "typed") return true;
	// A picked day's miss keeps the calendar: the details are not where the answer is.
	if (lookup.kind === "miss" && lookup.reason !== "NEED_DATE" && dateMode.kind !== "picker") return true;
	if (airDetailFields(d).some((f) => errors[f])) return true;
	return !!d.flight_id && !d.entry_point;
}

/**
 * Whether the details start open when the form mounts: everything
 * `detailsMustOpen` says, plus a leg with no flight whose details are already
 * filled (an existing manual leg — hiding them would hide the leg). Only at
 * mount: clearing a found flight to retype it must not pop the details open.
 */
export function detailsOpenByDefault(d: TravelLegDraft, lookup: LookupOutcome, errors: TravelLegErrors, dateMode?: AirDateMode): boolean {
	if (detailsMustOpen(d, lookup, errors, dateMode)) return true;
	return !d.flight_id && (!!d.entry_point || !!d.scheduled_at || d.origin.trim() !== "");
}

// ── The flight card ─────────────────────────────────────────────────────────

/** One end of a flight on its card: the airport code, a name when one is known, and the time (scheduled, and expected when it moved). */
export interface RouteEnd {
	code: string | null;
	name: string | null;
	terminal: string | null;
	scheduled: string | null;
	expected: string | null;
}

/**
 * The two ends of a flight as its card shows them. A flight row knows its
 * origin's name but not its destination's; the Games end is the leg's entry
 * point, so its name is used there when it is that airport (ARRIVAL: the
 * destination). The far end of a departure stays a bare code.
 */
export function routeEnds(direction: TravelDirection, flight: FlightSummary, entryPoint: Pick<EntryPointRef, "name" | "iata_code"> | null): { from: RouteEnd; to: RouteEnd } {
	const epAt = (code: string | null) => (entryPoint && code && entryPoint.iata_code?.toUpperCase() === code.toUpperCase() ? entryPoint.name : null);
	return {
		from: {
			code: flight.origin_iata,
			name: flight.origin_name ?? (direction === "DEPARTURE" ? epAt(flight.origin_iata) : null),
			terminal: null,
			scheduled: flight.scheduled_departure_at,
			expected: flight.expected_departure_at ?? flight.scheduled_departure_at,
		},
		to: {
			code: flight.destination_iata,
			name: direction === "ARRIVAL" ? epAt(flight.destination_iata) : null,
			terminal: flight.destination_terminal,
			scheduled: flight.scheduled_arrival_at,
			expected: flight.expected_arrival_at ?? flight.scheduled_arrival_at,
		},
	};
}

/** The Games airport of a leg's main flight: where it lands (ARRIVAL) / leaves from (DEPARTURE). */
export const gamesAirportOf = (direction: TravelDirection, flight: Pick<FlightSummary, "origin_iata" | "destination_iata">): string | null =>
	direction === "ARRIVAL" ? flight.destination_iata : flight.origin_iata;

/**
 * With a flight on the leg the entry point is filled from its terminal, and
 * only shown when there is a real choice to make: the Games airport has more
 * than one active entry point (terminals), so the guess may be wrong. (An
 * empty entry point or an error there opens it anyway: `detailsMustOpen`.)
 */
export function entryPointNeedsChoice(direction: TravelDirection, d: Pick<TravelLegDraft, "flight">, options: readonly Pick<EntryPointRef, "type" | "iata_code">[]): boolean {
	if (!d.flight) return false;
	const code = gamesAirportOf(direction, d.flight)?.toUpperCase();
	if (!code) return false;
	return options.filter((o) => o.type === "AIR" && o.iata_code?.toUpperCase() === code).length > 1;
}

/** "+ Add a connecting flight" under the found flight: an AIR leg on a flight, direct so far. */
export const offersFirstConnection = (d: Pick<TravelLegDraft, "mode" | "flight_id" | "connections">): boolean => d.mode === "AIR" && d.flight_id !== null && d.connections.length === 0;
